import React, { useState, useEffect } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";

// Page components
import AirdropForm from "./AirdropForm";
import AirdropHistory from "./AirdropHistory";
import ReverseAirdrop from "./ReverseAirdrop";

// Shared imports
import { TABS, API_ENDPOINTS } from "./constants";
import { colors, tabStyles, pageHeaderStyles } from "./styles";
import { NotificationModal } from "./components";
import "./styles.scss";

const AgenticPaymentAirdrop = ({
  payoutWalletAddress,
  payoutWalletId,
  adminEmail,
  hederaNetwork = "testnet",
  hederaChainId,
  initialCurrencies = [],
}) => {
  const [activeTab, setActiveTab] = useState(TABS.AIRDROP);
  // Initialize with server-side preloaded currencies if available
  const [currencies, setCurrencies] = useState(initialCurrencies);
  const [hederaWallets, setHederaWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  // If we have initial currencies, don't show loading state
  const [currenciesLoading, setCurrenciesLoading] = useState(
    initialCurrencies.length === 0,
  );
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Only fetch currencies if not preloaded
    if (initialCurrencies.length === 0) {
      fetchCurrencies();
    }
    fetchHederaWallets();
  }, []);

  const fetchCurrencies = async () => {
    setCurrenciesLoading(true);
    try {
      const data = await getRequest(API_ENDPOINTS.AVAILABLE_CURRENCIES);
      if (data.success) {
        setCurrencies(data.data);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    } finally {
      setCurrenciesLoading(false);
    }
  };

  const fetchHederaWallets = async (page = 1) => {
    setWalletsLoading(true);
    try {
      // Fetch all wallets with pagination (fetch all pages for wallet selector)
      let allWallets = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const data = await getRequest(
          `${API_ENDPOINTS.HEDERA_WALLETS}?page=${currentPage}&per_page=500`,
        );
        if (data.success) {
          allWallets = [...allWallets, ...data.data];
          const totalPages = data.meta?.total_pages || 1;
          currentPage++;
          hasMore = currentPage <= totalPages;

          // Safety limit to prevent infinite loops
          if (currentPage > 100) break;
        } else {
          hasMore = false;
        }
      }

      setHederaWallets(allWallets);
    } catch (error) {
      console.error("Error fetching hedera wallets:", error);
    } finally {
      setWalletsLoading(false);
    }
  };

  const fetchTokenBalance = async (tokenId) => {
    if (!tokenId) return null;
    try {
      return await getRequest("/admin/agentic_payment_airdrop/payout_info");
    } catch (error) {
      console.error("Error fetching token balance:", error);
      return null;
    }
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const handleExecuteAirdrop = async (
    recipients,
    amount,
    currency,
    tokenId,
  ) => {
    setLoading(true);
    try {
      const result = await postRequest(
        "/admin/agentic_payment_airdrop/execute_airdrop",
        { recipients, amount: amount.toString(), currency },
      );

      if (result.success) {
        const data = result.data || {};
        const successCount = data.successCount || 0;
        const failureCount = data.failureCount || 0;
        const totalCount = successCount + failureCount;

        let notificationType = "success";
        if (failureCount > 0 && successCount === 0) {
          notificationType = "error";
        } else if (failureCount > 0) {
          notificationType = "warning";
        }

        showNotification(
          `Airdrop completed: ${successCount} of ${totalCount} successful${
            failureCount > 0 ? `, ${failureCount} failed` : ""
          }`,
          notificationType,
        );
        return data;
      } else {
        const errorMsg = result.error || result.message || "Airdrop failed";
        showNotification(
          Array.isArray(errorMsg) ? errorMsg.join(", ") : errorMsg,
          "error",
        );
        return null;
      }
    } catch (error) {
      console.error("Airdrop error:", error);
      showNotification(error?.message || "Airdrop failed", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.AIRDROP:
        return (
          <AirdropForm
            currencies={currencies}
            currenciesLoading={currenciesLoading}
            hederaWallets={hederaWallets}
            walletsLoading={walletsLoading}
            payoutWalletAddress={payoutWalletAddress}
            onAirdrop={handleExecuteAirdrop}
            onFetchBalance={fetchTokenBalance}
            loading={loading}
          />
        );
      case TABS.HISTORY:
        return <AirdropHistory hederaNetwork={hederaNetwork} />;
      case TABS.REVERSE:
        return (
          <ReverseAirdrop
            currencies={currencies}
            showNotification={showNotification}
            hederaNetwork={hederaNetwork}
            payoutWalletId={payoutWalletId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="liquidity-pool-airdrop">
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>
          Agentic Payment Airdrop Management
        </h1>
        <div style={pageHeaderStyles.infoBox}>
          <span style={pageHeaderStyles.infoLabel}>Payout Account:</span>
          <code style={pageHeaderStyles.infoValue}>{payoutWalletAddress}</code>
        </div>
      </div>

      <NotificationModal
        notification={notification}
        onClose={closeNotification}
      />

      <div style={tabStyles.container}>
        <button
          type="button"
          onClick={() => setActiveTab(TABS.AIRDROP)}
          style={{
            ...tabStyles.tab,
            ...(activeTab === TABS.AIRDROP
              ? tabStyles.tabActive
              : tabStyles.tabInactive),
            borderRight: `1px solid ${colors.borderLight}`,
          }}
        >
          Airdrop Users
        </button>
        <button
          type="button"
          onClick={() => setActiveTab(TABS.HISTORY)}
          style={{
            ...tabStyles.tab,
            ...(activeTab === TABS.HISTORY
              ? tabStyles.tabActive
              : tabStyles.tabInactive),
            borderRight: `1px solid ${colors.borderLight}`,
          }}
        >
          Airdrop History
        </button>
        <button
          type="button"
          onClick={() => setActiveTab(TABS.REVERSE)}
          style={{
            ...tabStyles.tab,
            ...(activeTab === TABS.REVERSE
              ? tabStyles.tabActive
              : tabStyles.tabInactive),
          }}
        >
          Reverse Airdrop
        </button>
      </div>

      <div className="tab-content">{renderTabContent()}</div>
    </div>
  );
};

export default AgenticPaymentAirdrop;
